import { type StopAppointment } from '../appointments/domain/Appointment'
import { isZero } from '../utils/number'
import { isNotEmpty, isString } from '../utils/string'
import { type MakePropRequired } from '../utils/types/MakePropRequired'
import { type Location } from './Location'

const DEFAULT_STOPS_COUNT = 2

export type StopType = 'pickup' | 'delivery'

export type Stop = {
  address?: string
  checkedInAt?: Date
  checkedOutAt?: Date
  city: string
  country?: string
  date?: string
  fullStreetAddress?: string
  latitude?: string
  longitude?: string
  state: string
  stopIndex?: number
  zipCode?: string
  facilityName?: string
  appointment?: StopAppointment
}

export function createStop(city: string, state: string, rest?: Omit<Stop, 'city' | 'state'>): Stop {
  return { city, state, ...rest }
}

export function getCheckSteps(stops: Array<Stop>) {
  let checkIn = false
  let checkOut = false

  for (const { checkedInAt, checkedOutAt } of stops) {
    if (!checkedInAt) {
      checkIn = true
      break
    }

    if (!checkedOutAt) {
      checkOut = true
      break
    }
  }

  return { checkIn, checkOut }
}

export function getDelivery(stops: Array<Stop>): Stop {
  return stops[stops.length - 1]
}

export function getExtraStops(stops: Array<Stop>): Array<Stop> {
  if (!isMultiStop(stops)) {
    return []
  }

  return stops.slice(1, -1)
}

export function getPickup(stops: Array<Stop>): Stop {
  return stops[0]
}

export function hasAddress(stop: Stop): stop is MakePropRequired<Stop, 'address'> {
  const { address } = stop
  return isString(address) && isNotEmpty(address)
}

export function hasCheckedIn(stops: Array<Stop>): boolean {
  return stops.some(stop => stop.checkedInAt)
}

export function hasCoordinates(
  stop: Stop
): stop is MakePropRequired<Stop, 'latitude' | 'longitude'> {
  const { latitude, longitude } = stop
  const hasLatitude = isString(latitude) && isNotEmpty(latitude) && !isZero(Number(latitude))
  const hasLongitude = isString(longitude) && isNotEmpty(longitude) && !isZero(Number(longitude))

  return hasLatitude && hasLongitude
}

export function hasDate(stop: Stop): stop is MakePropRequired<Stop, 'date'> {
  return isString(stop.date) && isNotEmpty(stop.date)
}

export function isDelivery(stopIndex: number, stops: Array<Stop>): boolean {
  return stopIndex === stops.length - 1
}

export function isMultiStop(stops: Array<Stop>): boolean {
  return stops.length > DEFAULT_STOPS_COUNT
}

export function isPickup(stopIndex: number): boolean {
  return stopIndex === 0
}

export function getStopFromLocation(location: Location): Stop {
  const { address, city = '', country, lat, lng, state = '', zipcode } = location

  return {
    city,
    state,
    address,
    country,
    latitude: String(lat),
    longitude: String(lng),
    zipCode: zipcode,
  }
}
